import React, { useEffect, useRef, useState } from 'react';

import styles from 'components/questions/questions.module.css';
import { useTranslation } from 'react-i18next';
import { QuestionItem } from 'components/questions/QuestionItem';

import { convertToStringOrNull } from 'utils/dataTypes.utils';
import { ThinkingEmoji } from 'components/vectors/ThinkingEmoji';
import { MobileEmoji } from 'components/vectors/MobileEmoji';
import { OptimizationIcon } from 'components/vectors/OptimizationIcon';
import { CodeIcon } from 'components/vectors/CodeIcon';

export const QuestionsList = () => {
    const listRef = useRef<HTMLUListElement>(null);
    const [animatedItems, setAnimatedItems] = useState<string[]>([]);
    const { t } = useTranslation();

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const targetId = entry.target.getAttribute('id');
                        if (targetId) {
                            setAnimatedItems(prevItems => [
                                ...prevItems,
                                targetId,
                            ]);
                        }
                    }
                });
            },
            {
                threshold: 0.1,
            }
        );

        listRef.current?.querySelectorAll('li').forEach(item => {
            observer.observe(item);
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    const getAnimatedClassName = (id: number) => {
        return convertToStringOrNull(
            animatedItems.includes(id.toString()),
            `${styles.animated}`
        );
    };

    const questionsData = [
        { id: 1, text: t('about.questions.needAWebsite') },
        {
            id: 2,
            text: t('about.questions.butDontKnow'),
            emoji: <ThinkingEmoji />,
        },
        { id: 3, text: t('about.questions.dontLikeHow') },
        {
            id: 4,
            text: t('about.questions.onMobile'),
            iconAfter: <MobileEmoji />,
        },
        {
            id: 5,
            text: t('about.questions.needWebsiteOptimization'),
            iconBefore: <OptimizationIcon />,
        },
        { id: 6, text: t('about.questions.haveAWebsiteBut') },
        {
            id: 7,
            text: t('about.questions.dontKnowHowToCode'),
            emoji: <CodeIcon />,
        },
    ];

    return (
        <div className={styles.info}>
            <div className={styles.info__questions}>
                <ul className={`${styles.questions}`} ref={listRef}>
                    {questionsData.map(
                        ({ id, text, emoji, iconBefore, iconAfter }) => {
                            return (
                                <QuestionItem
                                    key={id}
                                    id={id}
                                    text={text}
                                    emoji={emoji}
                                    animatedClassName={getAnimatedClassName(id)}
                                    iconBefore={iconBefore}
                                    iconAfter={iconAfter}
                                />
                            );
                        }
                    )}
                </ul>
            </div>
            <div className={styles.info__text}>
                {t('about.workWithUsAndSeeText')}
            </div>
        </div>
    );
};
